/**
 * Floor-position curves ("height", F§7, R§2.3) shared by both speed integrators (W1-05a engine-eval).
 *
 * An integrator produces keyframes (time f64, height stored as f32, tween) that Phira evaluates with the ordinary
 * Anim rules (anim.ts): cursor = last keyframe with time <= t for sorted keyframes, 0 before the first keyframe
 * (extrapolation), the last keyframe for a NaN time (Phira's `set_time` loops); H = f32 lerp of the two heights by
 * the tween output, the last height held after the last keyframe. The heights themselves were accumulated in f64
 * by the integrator; only the stored values are f32. Keyframes are NOT always sorted: speed keyframes after
 * max_time put the final (max_time, height, hold) keyframe after later ones (F§7.2 hazards); such tracks replay
 * Phira's forward play from a fresh track (`freshWalk`: t = 0 keeps cursor 0, other times walk forward), which is
 * what Phira shows while playing forward from the start.
 *
 * Floor tweens live in a typed-array table dispatched by numeric kind (A§7.1: no closure per keyframe), with
 * every slice constant precomputed once:
 *   FT_LINEAR       y = x
 *   FT_HOLD         y = 0 (the final keyframe)
 *   FT_RAMP         ClampedTween(InQuad | OutQuad, lo..hi): the exact normalised integral of a linear speed ramp
 *                   (`speed_linear_tween`, both integrators)
 *   FT_MODERN       170 Modern eased segment: partial(x) = a·I(x) + b·x, I the event tween's integral
 *   FT_LEGACY170    170 Legacy-170 eased segment: partial(x) = a·f(x) + b·x, f the event tween
 * (eased segments: y(x) = partial(x) / total, 0 for x <= 0, 1 for x >= 1, x when not finite; F§7.3).
 *
 * Floor tweens carry their derivative, so the effective scroll speed dH/dt (what actually moves the notes: a
 * step for rpe170 easingType 0, a·f(x) + b in the Modern sub-mode, a·f'(x) + b in the Legacy-170 sub-mode, the
 * linear ramp of the legacy integrator) is evaluated analytically instead of by differencing f32 heights. At a
 * segment's start (x = 0) the derivative is the right derivative, so beat-aligned samples read the start speed.
 *
 * Internal API (W1-05b queries `floorPosition`, `sampleFloor`, `sampleEffectiveSpeed`; W1-06 note heights):
 *   FloorCurve.heightAt(t)        H(t), f32, Phira height units (1 = 450 RPE-y px)
 *   FloorCurve.speedAt(t)         dH/dt in height units per second
 *   sampleFloor / sampleEffectiveSpeed (RPE speed units = dH/dt / SPEED_RATIO)
 *   FloorTweenBuilder / FloorTweenTable / FloorTrack (integrators)
 */

import { createBezierCurve, type BezierCurve } from '../../ease/bezier';
import { generalIntY, intClampedY, intStaticY } from '../../ease/integrals';
import { HOLD_TWEEN, LINEAR_TWEEN } from '../../ease/tweens';
import type { PhiraTweenDesc, Speed170Mode, TweenId } from '../../ease/types';
import { F32 } from '../../math/f32';
import { Search } from '../../math/search';
import { freshWalk, sampleTime, TweenTable } from '../anim';
import { SPEED_RATIO_F32 } from '../channels';

const fr = Math.fround;

// ---------------------------------------------------------------------------------------------------------------
// Tween table
// ---------------------------------------------------------------------------------------------------------------

/** Floor tween kinds (numeric dispatch). */
export const FT_LINEAR = 0;
export const FT_HOLD = 1;
export const FT_RAMP = 2;
export const FT_MODERN = 3;
export const FT_LEGACY170 = 4;

/** Integral kinds of a Modern eased segment (Phira's IntStatic / IntClamped / GeneralInt tweens). */
const INT_STATIC = 0;
const INT_CLAMPED = 1;
const INT_BEZIER = 2;

/** Phira tween ids of the quadratic slices. */
const IN_QUAD: TweenId = 6;
const OUT_QUAD: TweenId = 7;

/** Speed-ramp threshold: |s - e| below it is a constant speed (linear height). */
const RAMP_EPS = fr(1e-5);

/** Half-width of the difference window of the Legacy-170 slope readout (f64, clamped to [0, 1]). */
const SLOPE_H = 1e-3;

/**
 * An eased 170 speed segment as plain data (`speed_segment_tween`): partial(x) = a·G(x) + b·x in f32, with
 * G = the integral of the event tween (Modern) or the event tween itself (Legacy-170); total = partial(1).
 */
export interface EasedSegment {
  readonly mode: Speed170Mode;
  /** The event tween as Phira resolves it. */
  readonly desc: PhiraTweenDesc;
  /** The prepared curve of a bezier event (the global registry's curve), else null. */
  readonly curve: BezierCurve | null;
  /** Coefficients of partial(x) (f32): Modern a = e - s, b = s; Legacy-170 a = k, b = b. */
  readonly a: number;
  readonly b: number;
  /** partial(1) (f32): the segment's average speed in height units per second. */
  readonly total: number;
}

/**
 * The curve `generalIntY` integrates, set only for the duration of one `bezierIntegral` call. GeneralInt takes a
 * plain function; this single module-level adapter replaces a closure per bezier keyframe (A§7.1). Evaluation is
 * synchronous and `BezierCurve.y` never integrates, so the slot cannot be re-entered.
 */
let intCurve: BezierCurve | null = null;

function intCurveY(x: number): number {
  return intCurve === null ? Number.NaN : intCurve.y(x);
}

/** Phira's GeneralInt tween of a bezier curve at x: the 3-point Gauss-Legendre integral of y over [0, x] (f32). */
function bezierIntegral(curve: BezierCurve, x: number): number {
  intCurve = curve;
  try {
    return generalIntY(intCurveY, x);
  } finally {
    intCurve = null;
  }
}

/** Floor keyframe tweens as flat typed arrays. */
export class FloorTweenTable {
  readonly n: number;
  readonly kind: Uint8Array;
  /** Ramps: ClampedTween(InQuad | OutQuad, lo..hi). Eased segments: the event tween f (slice constants ready). */
  readonly f: TweenTable;
  /** Eased Modern segments: which integral, its tween id and slice. */
  readonly intKind: Uint8Array;
  readonly intId: Uint8Array;
  readonly intL: Float32Array;
  readonly intR: Float32Array;
  /** Ramps: dy(x) = c0 + c1·x (f64). Eased segments: the partial coefficients a (c0) and b (c1), f32 values. */
  readonly c0: Float64Array;
  readonly c1: Float64Array;
  /** Eased segments: partial(1) (f32). */
  readonly total: Float32Array;

  constructor(n: number) {
    this.n = n;
    this.kind = new Uint8Array(n);
    this.f = new TweenTable(n);
    this.intKind = new Uint8Array(n);
    this.intId = new Uint8Array(n);
    this.intL = new Float32Array(n);
    this.intR = new Float32Array(n);
    this.c0 = new Float64Array(n);
    this.c1 = new Float64Array(n);
    this.total = new Float32Array(n);
  }

  setLinear(i: number): void {
    this.kind[i] = FT_LINEAR;
    this.f.setStatic(i, LINEAR_TWEEN);
  }

  setHold(i: number): void {
    this.kind[i] = FT_HOLD;
    this.f.setStatic(i, HOLD_TWEEN);
  }

  /** ClampedTween(OutQuad | InQuad, l..r) with its analytic derivative. */
  setRamp(i: number, out: boolean, l: number, r: number): void {
    const lo = fr(l);
    const hi = fr(r);
    this.kind[i] = FT_RAMP;
    this.f.setClamped(i, out ? OUT_QUAD : IN_QUAD, lo, hi);
    // y(x) = (q(lo + span·x) - q(lo)) / den with q(u) = u^2 (In) or 1 - (1 - u)^2 (Out): dy is linear in x.
    const span = hi - lo;
    const q = (u: number): number => (out ? 1 - (1 - u) * (1 - u) : u * u);
    const den = q(hi) - q(lo);
    const k = (2 * span) / den;
    this.c0[i] = out ? k * (1 - lo) : k * lo;
    this.c1[i] = out ? -k * span : k * span;
  }

  /** An eased 170 segment. */
  setEased(i: number, seg: EasedSegment): void {
    this.kind[i] = seg.mode === 'modern' ? FT_MODERN : FT_LEGACY170;
    const d = seg.desc;
    switch (d.kind) {
      case 'static':
        this.f.setStatic(i, d.tween);
        this.intKind[i] = INT_STATIC;
        this.intId[i] = d.tween;
        break;
      case 'clamped':
        this.f.setClamped(i, d.tween, d.l, d.r);
        this.intKind[i] = INT_CLAMPED;
        this.intId[i] = d.tween;
        this.intL[i] = d.l;
        this.intR[i] = d.r;
        break;
      case 'bezier': {
        // The curve is stored once in `f.curves`; GeneralInt reads it through the shared adapter below.
        this.f.setCurve(i, seg.curve ?? createBezierCurve(d.p));
        this.intKind[i] = INT_BEZIER;
        break;
      }
    }
    this.c0[i] = seg.a;
    this.c1[i] = seg.b;
    this.total[i] = seg.total;
  }

  /** Integral of the event tween of eased keyframe i from 0 to x (f32). */
  private integral(i: number, x: number): number {
    switch (this.intKind[i]) {
      case INT_STATIC:
        return intStaticY(this.intId[i] ?? LINEAR_TWEEN, x);
      case INT_CLAMPED:
        return intClampedY(this.intId[i] ?? LINEAR_TWEEN, this.intL[i] ?? 0, this.intR[i] ?? 1, x);
      default: {
        const curve = this.f.curves[i];
        return curve === null || curve === undefined ? Number.NaN : bezierIntegral(curve, x);
      }
    }
  }

  /** partial(x) of eased keyframe i (f32): a·G(x) + b·x. */
  partial(i: number, x: number): number {
    const g = this.kind[i] === FT_MODERN ? this.integral(i, x) : this.f.y(i, x);
    return fr(fr((this.c0[i] ?? Number.NaN) * g) + fr((this.c1[i] ?? Number.NaN) * x));
  }

  /** Tween output of keyframe i at progress x (f32, x unclamped). */
  y(i: number, x: number): number {
    switch (this.kind[i]) {
      case FT_LINEAR:
        return fr(x);
      case FT_HOLD:
        return 0;
      case FT_RAMP:
        return this.f.y(i, x);
      default: {
        const xx = fr(x);
        if (xx <= 0) return 0;
        if (xx >= 1) return 1;
        const v = fr(this.partial(i, xx) / (this.total[i] ?? Number.NaN));
        return Number.isFinite(v) ? v : xx;
      }
    }
  }

  /**
   * dy/dx of keyframe i at progress x (f64). Eased segments: (a·shape(x) + b) / total on [0, 1) (the right
   * derivative at x = 0), 0 outside where the height tween is clamped flat.
   */
  dy(i: number, x: number): number {
    switch (this.kind[i]) {
      case FT_LINEAR:
        return 1;
      case FT_HOLD:
        return 0;
      case FT_RAMP:
        return (this.c0[i] ?? Number.NaN) + (this.c1[i] ?? Number.NaN) * x;
      default: {
        if (!(x >= 0 && x < 1)) return 0;
        const shape = this.kind[i] === FT_MODERN ? this.f.y(i, x) : this.slope(i, x);
        return ((this.c0[i] ?? Number.NaN) * shape + (this.c1[i] ?? Number.NaN)) / (this.total[i] ?? Number.NaN);
      }
    }
  }

  /**
   * f'(x) of the event tween of keyframe i for readouts: a difference over [x - h, x + h] clamped to [0, 1]
   * (one-sided at the ends), so curves that are undefined outside [0, 1] (Circ: sqrt of a negative) stay finite.
   */
  slope(i: number, x: number): number {
    const l = Math.max(x - SLOPE_H, 0);
    const r = Math.min(x + SLOPE_H, 1);
    if (!(r > l)) return Number.NaN;
    return (this.f.y(i, r) - this.f.y(i, l)) / (r - l);
  }

  /** Readable name of keyframe i's tween (tests, debug views). */
  label(i: number): string {
    switch (this.kind[i]) {
      case FT_LINEAR:
        return 'linear';
      case FT_HOLD:
        return 'hold';
      case FT_RAMP: {
        const lo = this.f.lo[i] ?? Number.NaN;
        const hi = fr(lo + (this.f.span[i] ?? Number.NaN));
        return `clamped ${this.f.id[i] === OUT_QUAD ? 'OutQuad' : 'InQuad'} ${String(lo)}..${String(hi)}`;
      }
      case FT_MODERN:
        return 'rpe170 modern';
      default:
        return 'rpe170 legacy';
    }
  }
}

/** Staging codes of the builder (one per pushed tween). */
const B_LINEAR = 0;
const B_HOLD = 1;
const B_RAMP_IN = 2;
const B_RAMP_OUT = 3;
const B_EASED = 4;

/** Growable staging area for the tweens of a track under construction (the integrators push in time order). */
export class FloorTweenBuilder {
  private readonly codes: number[] = [];
  private readonly lo: number[] = [];
  private readonly hi: number[] = [];
  private readonly eased: (EasedSegment | null)[] = [];

  get length(): number {
    return this.codes.length;
  }

  private push(code: number, lo: number, hi: number, eased: EasedSegment | null): this {
    this.codes.push(code);
    this.lo.push(lo);
    this.hi.push(hi);
    this.eased.push(eased);
    return this;
  }

  linear(): this {
    return this.push(B_LINEAR, 0, 0, null);
  }

  hold(): this {
    return this.push(B_HOLD, 0, 0, null);
  }

  /**
   * Speed-ramp tween from the start / end speeds s, e (f32), shared by the legacy integrator and the 170 linear
   * case: |s - e| < 1e-5 -> linear; |s| > |e| -> ClampedTween(OutQuad, 0..1 - e/s); otherwise
   * ClampedTween(InQuad, s/e..1).
   */
  ramp(s: number, e: number): this {
    if (Math.abs(fr(s - e)) < RAMP_EPS) return this.linear();
    if (Math.abs(s) > Math.abs(e)) return this.push(B_RAMP_OUT, 0, fr(1 - fr(e / s)), null);
    return this.push(B_RAMP_IN, fr(s / e), 1, null);
  }

  easedSegment(seg: EasedSegment): this {
    return this.push(B_EASED, 0, 0, seg);
  }

  /** Drops the last tween (an integrator overwriting its last keyframe). */
  pop(): void {
    this.codes.pop();
    this.lo.pop();
    this.hi.pop();
    this.eased.pop();
  }

  build(): FloorTweenTable {
    const n = this.codes.length;
    const t = new FloorTweenTable(n);
    for (let i = 0; i < n; i++) {
      const seg = this.eased[i] ?? null;
      switch (this.codes[i]) {
        case B_LINEAR:
          t.setLinear(i);
          break;
        case B_RAMP_IN:
          t.setRamp(i, false, this.lo[i] ?? Number.NaN, this.hi[i] ?? Number.NaN);
          break;
        case B_RAMP_OUT:
          t.setRamp(i, true, this.lo[i] ?? Number.NaN, this.hi[i] ?? Number.NaN);
          break;
        case B_EASED:
          if (seg !== null) t.setEased(i, seg);
          else t.setHold(i);
          break;
        default:
          t.setHold(i);
      }
    }
    return t;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Tracks and curves
// ---------------------------------------------------------------------------------------------------------------

/** One keyframe track of heights. */
export class FloorTrack {
  readonly times: Float64Array;
  /** f32 heights. */
  readonly values: Float32Array;
  readonly tweens: FloorTweenTable;
  readonly sorted: boolean;
  private hint = 0;

  constructor(times: readonly number[], values: readonly number[], tweens: FloorTweenTable) {
    this.times = Float64Array.from(times);
    this.values = Float32Array.from(values);
    this.tweens = tweens;
    let sorted = true;
    for (let i = 1; i < this.times.length; i++) {
      if (!((this.times[i] ?? Number.NaN) >= (this.times[i - 1] ?? Number.NaN))) sorted = false;
    }
    this.sorted = sorted;
  }

  get length(): number {
    return this.times.length;
  }

  private valid(i: number, t: number): boolean {
    const n = this.times.length;
    return (i === 0 || (this.times[i] ?? Number.NaN) <= t) && (i + 1 >= n || (this.times[i + 1] ?? Number.NaN) > t);
  }

  /**
   * Active keyframe at t: the last keyframe with time <= t for sorted tracks (0 before the first), the last
   * keyframe for a NaN t; unsorted tracks replay Phira's forward play from a fresh track (`freshWalk`: t = 0 keeps
   * cursor 0, other times walk forward past keyframes that are not later than t, NaN keyframe times included).
   */
  cursorAt(t: number): number {
    const n = this.times.length;
    if (n <= 1) return 0;
    if (Number.isNaN(t)) return n - 1;
    if (!this.sorted) return freshWalk(this.times, t);
    const h = this.hint;
    if (h < n && this.valid(h, t)) return h;
    if (h + 1 < n && this.valid(h + 1, t)) {
      this.hint = h + 1;
      return h + 1;
    }
    let i = Search.upperBound(this.times, t) - 1;
    if (i < 0) i = 0;
    this.hint = i;
    return i;
  }

  heightAt(t: number): number {
    const n = this.times.length;
    if (n === 0) return 0;
    const i = this.cursorAt(t);
    const v1 = this.values[i] ?? Number.NaN;
    if (i + 1 >= n) return v1;
    const t1 = this.times[i] ?? Number.NaN;
    const t2 = this.times[i + 1] ?? Number.NaN;
    const y = this.tweens.y(i, fr((t - t1) / (t2 - t1)));
    return F32.lerp(v1, this.values[i + 1] ?? Number.NaN, y);
  }

  speedAt(t: number): number {
    const n = this.times.length;
    if (n <= 1) return 0;
    const i = this.cursorAt(t);
    if (i + 1 >= n) return 0;
    const t1 = this.times[i] ?? Number.NaN;
    const t2 = this.times[i + 1] ?? Number.NaN;
    const dh = (this.values[i + 1] ?? Number.NaN) - (this.values[i] ?? Number.NaN);
    return (dh * this.tweens.dy(i, (t - t1) / (t2 - t1))) / (t2 - t1);
  }
}

export type FloorKind = 'none' | 'legacy' | 'rpe170-modern' | 'rpe170-legacy';

export interface FloorCurve {
  /** Which integrator produced the curve ('none': no speed list, H = 0). */
  readonly kind: FloorKind;
  /** The max_time the curve was built for (heights freeze there). */
  readonly maxTime: number;
  /** One track (legacy) or one per speed layer (170: heights summed right-associatively in f32). */
  readonly tracks: readonly FloorTrack[];
  /** H(t), f32. */
  heightAt(t: number): number;
  /** dH/dt (height units per second). */
  speedAt(t: number): number;
}

class SummedFloor implements FloorCurve {
  readonly kind: FloorKind;
  readonly maxTime: number;
  readonly tracks: readonly FloorTrack[];
  private readonly scratch: Float64Array;

  constructor(kind: FloorKind, maxTime: number, tracks: readonly FloorTrack[]) {
    this.kind = kind;
    this.maxTime = maxTime;
    this.tracks = tracks;
    this.scratch = new Float64Array(tracks.length);
  }

  heightAt(t: number): number {
    const tracks = this.tracks;
    if (tracks.length === 0) return 0;
    if (tracks.length === 1) return tracks[0]?.heightAt(t) ?? 0;
    for (let i = 0; i < tracks.length; i++) this.scratch[i] = tracks[i]?.heightAt(t) ?? 0;
    return F32.sumChain(this.scratch, tracks.length);
  }

  speedAt(t: number): number {
    let v = 0;
    for (const tr of this.tracks) v += tr.speedAt(t);
    return v;
  }
}

/** A floor curve from tracks (legacy: one track; 170: one per layer). */
export function floorCurve(kind: FloorKind, maxTime: number, tracks: readonly FloorTrack[]): FloorCurve {
  return new SummedFloor(kind, maxTime, tracks);
}

/** H = 0 everywhere (a line without speed lists: its notes sit on the line). */
export function zeroFloor(maxTime: number): FloorCurve {
  return new SummedFloor('none', maxTime, []);
}

/** H(t) at out.length evenly spaced times over [t0, t1]. */
export function sampleFloor(curve: FloorCurve, t0: number, t1: number, out: Float32Array | Float64Array): void {
  const n = out.length;
  for (let i = 0; i < n; i++) out[i] = curve.heightAt(sampleTime(t0, t1, i, n));
}

/** Effective scroll speed dH/dt in RPE speed units (dH/dt / SPEED_RATIO) at evenly spaced times over [t0, t1]. */
export function sampleEffectiveSpeed(
  curve: FloorCurve,
  t0: number,
  t1: number,
  out: Float32Array | Float64Array,
): void {
  const n = out.length;
  for (let i = 0; i < n; i++) out[i] = curve.speedAt(sampleTime(t0, t1, i, n)) / SPEED_RATIO_F32;
}

/** Effective scroll speed at t in RPE speed units. */
export function effectiveSpeedAt(curve: FloorCurve, t: number): number {
  return curve.speedAt(t) / SPEED_RATIO_F32;
}
